import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { oneDaysMilli } from '../constants-types/constants';
import { ConfigurationService } from '../system-configuration/Configuration.service';

export enum DateFormatUtilityRezoneInfo {
    AmericaEST = 'America/New_York'
}

@Injectable({ providedIn: 'root' })
export class DateFormatUtility {
    monthNameFormat;

    constructor(config: ConfigurationService) {
        this.monthNameFormat = new Intl.DateTimeFormat(config.currentLocale, {
            month: 'long'
        });
    }

    // Transform the given Date into format: MMM dd y
    // Example output: Apr 21 2017

    getMonthString(monthNumber: number): string {
        // snagged from https://stackoverflow.com/questions/47232534/how-to-get-a-list-of-month-names-in-javascript-using-intl
        // This code should be redone, but working to just preserve behavior
        const monthName = this.monthNameFormat.format(
            new Date(Date.UTC(2000, monthNumber, 15, 0, 0))
        );
        return monthName.charAt(0).toLocaleUpperCase() + monthName.slice(1);
    }

    transformDate(date: Date | string) {
        if (typeof date === 'string') {
            return date;
        } else {
            return date
                ? date.getFullYear() +
                      ' ' +
                      this.getMonthString(date.getMonth()) +
                      ' ' +
                      date.getDate()
                : '';
        }
    }

    transformDateString(dateStr: string): string {
        try {
            return this.transformDate(new Date(dateStr));
        } catch {
            return dateStr;
        }
    }

    // Returns DateTime object in 'America EST' format.
    getEstLuxonDateTime(date: string): DateTime {
        return this.getRezonedLuxonDateTime(
            date,
            DateFormatUtilityRezoneInfo.AmericaEST
        );
    }

    getRezonedLuxonDateTime(
        date: string,
        zone: DateFormatUtilityRezoneInfo | string
    ) {
        const zoneName = zone.valueOf();
        return DateTime.fromISO(date, {
            zone: zoneName
        });
    }

    getNowPlusDays(days: number): Date {
        const returnDate = new Date();
        returnDate.setDate(returnDate.getDate() + days);
        return returnDate;
    }

    addDaysToDate(date: Date, days: number) {
        const newDate = new Date(date);
        newDate.setDate(newDate.getDate() + days);
        return newDate;
    }

    getDateDiff(endDate: Date): number {
        return Math.round(
            Math.abs(
                (endDate.getTime() - new Date(Date.now()).getTime()) /
                    oneDaysMilli
            )
        );
    }

    getTimeIn12HourFormat(date: Date) {
        let hours = date.getHours();
        const minutes = date.getMinutes();

        const amOrpmDisplay = hours >= 12 ? 'pm' : 'am';

        hours = hours % 12;
        const hoursDisplay = hours ? hours : 12;

        const minutesDisplay = minutes < 10 ? '0' + minutes : minutes;
        return `${hoursDisplay}: ${minutesDisplay}  ${amOrpmDisplay}`;
    }

    isXDaysPastDate(date: Date, days: number): boolean {
        const now = new Date().getTime();
        return date.getTime() + days * oneDaysMilli < now;
    }

    // Displays the {Month}{YYYY} format
    // Example: Input: 05/01/2019 Output: May 2019
    getMonthYearFormat(date: Date) {
        return date
            ? this.getMonthString(date.getMonth()) + ' ' + date.getFullYear()
            : '';
    }

    getMonthDayYearFormat(date: Date) {
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }

    getDayMonthNameYearFormat(date: Date) {
        return `${date.getDate()} ${this.getMonthString(
            date.getMonth()
        )} ${date.getFullYear()}`;
    }

    getMonthNameDayYearFormat(date: Date) {
        return `${this.getMonthString(
            date.getMonth()
        )} ${date.getDate()}, ${date.getFullYear()}`;
    }

    getMidnightOfDate(date: Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
}
